<template>
  <div class="cart-item-card">
    <img :src="item?.product?.thumbnail" alt="product" class="cart-item-image">

    <div class="cart-item-main">
      <div class="cart-item-copy">
        <p class="cart-item-title">{{ item?.product.name }}</p>
        <div class="cart-item-price-row">
          <p class="cart-item-price">฿{{ unitPrice }}</p>
          <template v-if="hasDiscount">
            <span class="cart-item-original-price">฿{{ item?.product.price }}</span>
            <span class="cart-item-discount-percent">-{{ item?.product.discount_percent }}%</span>
          </template>
        </div>
      </div>

      <div class="cart-item-actions">
        <div class="cart-item-quantity">
          <button type="button" @click="substract">-</button>
          <p class="cart-item-amount">{{ amount }}</p>
          <button type="button" @click="add">+</button>
        </div>

        <button
          type="button"
          class="cart-item-delete cart-item-delete-mobile"
          @click.stop.prevent="handleRemove"
          @touchend.stop.prevent="handleRemove"
          @pointerup.stop.prevent="handleRemove"
          aria-label="Delete product"
        >
          <img src="@/assets/icons/trash.svg" alt="delete" class="cart-item-trash-icon">
        </button>
      </div>
    </div>

    <div class="cart-item-side">
      <p class="cart-item-total">
        {{ t('myCart.shortTotal') }} {{ multiplyAndRound(amount, unitPrice) }}฿
      </p>
      <button
        type="button"
        class="cart-item-delete cart-item-delete-desktop"
        @click.stop.prevent="handleRemove"
        @touchend.stop.prevent="handleRemove"
        @pointerup.stop.prevent="handleRemove"
        aria-label="Delete product"
      >
        <img src="@/assets/icons/trash.svg" alt="delete" class="cart-item-trash-icon">
      </button>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

export default {
  props: {
    item: {
      type: Object,
      default: null,
    },
    getUser: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const store = useStore()
    const getUser = computed(() => store.getters['user/getUser'])
    const { t } = useI18n()
    const amount = ref(props.item?.amount)
    const isRemoving = ref(false)

    const hasDiscount = computed(() => !!props.item?.product?.is_discount_active)
    const unitPrice = computed(() => (
      props.item?.product?.effective_price ?? props.item?.product?.price
    ))

    const add = () => {
      amount.value++
      store.dispatch('cart/fetchIncreaseProduct', {
        product_id: props.item?.product?.id,
        product_name: props.item?.product?.name,
        user: getUser.value
      })
    }
    const substract = () => {
      amount.value--
      store.dispatch('cart/fetchDecreaseProduct', {
        product_id: props.item?.product?.id,
        product_name: props.item?.product?.name,
        user: getUser.value
      })
    }
    const removeItem = () => {
      store.dispatch('cart/fetchDeleteProduct', {
        product_id: props.item?.product?.id,
        product_name: props.item?.product?.name,
        user: getUser.value
      })
    }
    const handleRemove = () => {
      if (isRemoving.value) return
      isRemoving.value = true
      removeItem()
      setTimeout(() => {
        isRemoving.value = false
      }, 500)
    }
    const multiplyAndRound = (x, y) => `${(x * y).toFixed(2)}`

    return { t, amount, add, substract, handleRemove, multiplyAndRound, hasDiscount, unitPrice }
  }
}
</script>

<style lang="scss" scoped>
.cart-item-card {
  border: 1px solid #C3C3C3;
  border-radius: 10px;
  padding: 12px;
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 14px;
  background-color: #fff;
  @media screen and (min-width: 780px) {
    grid-template-columns: 88px minmax(0, 1fr) auto;
    align-items: center;
    border: 1px solid #F1F1F1;
    padding: 16px;
  }
}

.cart-item-image {
  width: 88px;
  height: 88px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.cart-item-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cart-item-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.cart-item-title {
  font-size: 16px;
  line-height: 1.3;
  font-weight: 500;
  color: #161616;
  word-break: break-word;
  @media screen and (min-width: 780px) {
    font-size: 16px;
  }
}

.cart-item-price-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cart-item-price {
  color: var(--error-color);
  font-size: 16px;
  line-height: 1;
  font-weight: 700;
}

.cart-item-original-price {
  font-size: 13px;
  color: #9A9A9A;
  text-decoration: line-through;
}

.cart-item-discount-percent {
  font-size: 11px;
  font-weight: bold;
  color: #FD6868;
  border: 1px solid #FD6868;
  border-radius: 3px;
  padding: 1px 4px;
  white-space: nowrap;
}

.cart-item-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cart-item-quantity {
  display: flex;
  align-items: center;
  > button {
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #C3C3C3;
    border-radius: 4px;
    color: #004890;
    outline: none;
    background-color: #fff;
    @media screen and (min-width: 780px) {
      border: 1px solid #004890;
    }
    &:active {
      opacity: 0.7;
    }
  }
}

.cart-item-amount {
  font-size: 20px;
  margin: 0 16px;
}

.cart-item-side {
  display: none;
  @media screen and (min-width: 780px) {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    min-width: 104px;
    height: 100%;
  }
}

.cart-item-total {
  color: #878787;
  font-size: 16px;
  text-align: right;
}

.cart-item-delete {
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  &:active {
    opacity: 0.7;
  }
}

.cart-item-delete-mobile {
  width: 64px;
  min-width: 64px;
  max-width: 64px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  margin-left: 12px;
  position: relative;
  z-index: 4;
  isolation: isolate;
  border-radius: 50%;
  transform: translateX(-8px);
  appearance: none;
  -webkit-appearance: none;
  &::before {
    content: "";
    position: absolute;
    top: 4px;
    left: 4px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
    z-index: 0;
  }
  @media screen and (min-width: 780px) {
    display: none;
  }
}

.cart-item-delete-desktop {
  display: none;
  @media screen and (min-width: 780px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
  }
}

.cart-item-trash-icon {
  width: 18px;
  height: 18px;
  display: block;
  margin: 0;
  filter: invert(30%) sepia(80%) saturate(800%) hue-rotate(330deg);
  pointer-events: none;
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  @media screen and (min-width: 780px) {
    position: static;
    transform: none;
  }
}
</style>
